import type { Bi } from './sectors'

export type Line = { text: Bi; accent?: boolean }

export const LOGO_BCITI = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png'
export const LOGO_ATNA = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png'

/* ---------- Screen 1 — Promise ---------- */
export const overviewHero = {
  eyebrow: { fr: 'Plateforme BCITI | ATNA', en: 'BCITI | ATNA Platform' } as Bi,
  headline: [
    { text: { fr: 'Plusieurs points d’entrée.', en: 'Many entry points.' } },
    { text: { fr: 'Une seule intelligence derrière.', en: 'One intelligence behind them.' }, accent: true },
  ] as Line[],
  body: {
    fr: 'BCITI | ATNA relie vos usagers, vos équipes, vos services, vos données et vos systèmes autour d’un même contexte de confiance.',
    en: 'BCITI | ATNA connects your users, teams, services, data and systems around one shared context of trust.',
  } as Bi,
}

/* ---------- Screen 2 — The architecture ---------- */
export type EntryKey = 'web' | 'portal' | 'mobile' | 'phone' | 'email' | 'social' | 'assistants'
export type CoreKey = 'identity' | 'record' | 'rules' | 'connectors' | 'atna'
export type DestinationKey = 'ops' | 'admin' | 'decisions'

export const overviewMap = {
  eyebrow: { fr: 'Comment la plateforme fonctionne', en: 'How the platform works' } as Bi,
  headline: [
    { text: { fr: 'Tout entre par des chemins différents.', en: 'Everything comes in by different paths.' } },
    { text: { fr: 'Tout devient du contexte exploitable.', en: 'Everything becomes usable context.' }, accent: true },
  ] as Line[],
  body: {
    fr: 'BCITI | ATNA agit comme le système nerveux numérique de l’organisation : elle relie l’identité, les données, les règles, les systèmes et l’intelligence pour que chaque interaction puisse nourrir la suivante.',
    en: 'BCITI | ATNA acts as the organization’s digital nervous system: it connects identity, data, rules, systems and intelligence so every interaction can inform the next one.',
  } as Bi,

  entryLabel: { fr: 'Plusieurs points d’entrée', en: 'Many entry points' } as Bi,
  entrySub: { fr: 'Tous les chemins alimentent le même contexte.', en: 'Every path feeds the same context.' } as Bi,
  entries: [
    { key: 'web', label: { fr: 'Web', en: 'Web' } },
    { key: 'portal', label: { fr: 'Portail', en: 'Portal' } },
    { key: 'mobile', label: { fr: 'Application mobile', en: 'Mobile app' } },
    { key: 'phone', label: { fr: 'Téléphone', en: 'Phone' } },
    { key: 'email', label: { fr: 'Courriel', en: 'Email' } },
    { key: 'social', label: { fr: 'Réseaux sociaux', en: 'Social media' } },
    { key: 'assistants', label: { fr: 'Assistants IA', en: 'AI assistants' } },
  ] as { key: EntryKey; label: Bi }[],

  coreLabel: { fr: 'Système nerveux numérique', en: 'Digital nervous system' } as Bi,
  core: [
    { key: 'identity', label: { fr: 'Identité et profils', en: 'Identity & profiles' } },
    { key: 'record', label: { fr: 'Dossier usager', en: 'User record' } },
    { key: 'rules', label: { fr: 'Règles d’affaires', en: 'Business rules' } },
    { key: 'connectors', label: { fr: 'Connecteurs', en: 'Connectors' } },
    { key: 'atna', label: { fr: 'Intelligence ATNA', en: 'ATNA Intelligence' } },
  ] as { key: CoreKey; label: Bi }[],

  servicesLabel: { fr: 'Services BCITI | ATNA', en: 'BCITI | ATNA services' } as Bi,
  services: [
    { fr: 'Requêtes', en: 'Requests' },
    { fr: 'Communications', en: 'Communications' },
    { fr: 'Collectes', en: 'Waste collection' },
    { fr: 'Loisirs', en: 'Recreation' },
    { fr: 'Permis', en: 'Permits' },
    { fr: 'Médailles et licences', en: 'Pet tags & licences' },
    { fr: 'Compteurs d’eau', en: 'Water meters' },
  ] as Bi[],

  ecosystemLabel: { fr: 'Votre écosystème', en: 'Your ecosystem' } as Bi,
  ecosystem: [
    { fr: 'ERP', en: 'ERP' },
    { fr: 'CRM', en: 'CRM' },
    { fr: 'Finances', en: 'Finance' },
    { fr: 'Systèmes métiers', en: 'Business systems' },
    { fr: 'Données ouvertes', en: 'Open data' },
    { fr: 'IoT', en: 'IoT' },
    { fr: 'API', en: 'APIs' },
    { fr: 'Partenaires', en: 'Partners' },
  ] as Bi[],
  ecosystemNote: [
    { fr: 'On connecte ce qui fonctionne.', en: 'We connect what works.' },
    { fr: 'On simplifie ce qui doit l’être.', en: 'We simplify what needs simplifying.' },
  ] as Bi[],

  destinationsLabel: { fr: 'Votre organisation', en: 'Your organization' } as Bi,
  destinations: [
    { key: 'ops', label: { fr: 'Équipes opérationnelles', en: 'Operations teams' } },
    { key: 'admin', label: { fr: 'Administration', en: 'Administration' } },
    { key: 'decisions', label: { fr: 'Décideurs', en: 'Decision-makers' } },
  ] as { key: DestinationKey; label: Bi }[],

  loop: { fr: 'Chaque interaction enrichit la suivante.', en: 'Every interaction enriches the next.' } as Bi,
}

/* ---------- Screen 3 — The value ---------- */
export const overviewCapacity = {
  eyebrow: { fr: 'Une plateforme qui augmente la capacité', en: 'A platform that increases capacity' } as Bi,
  headline: [
    { text: { fr: 'Chaque interaction', en: 'Every interaction' } },
    { text: { fr: 'rend l’écosystème plus intelligent.', en: 'makes the ecosystem smarter.' }, accent: true },
  ] as Line[],
  principles: [
    { n: '01', title: { fr: 'Connaître', en: 'Know' }, text: { fr: 'Une identité et un dossier usager qui suivent la relation.', en: 'An identity and a user record that follow the relationship.' } },
    { n: '02', title: { fr: 'Connecter', en: 'Connect' }, text: { fr: 'Vos systèmes existants restent connectés à une même architecture.', en: 'Your existing systems stay connected to one architecture.' } },
    { n: '03', title: { fr: 'Comprendre', en: 'Understand' }, text: { fr: 'Les interactions, données et règles deviennent du contexte exploitable par ATNA.', en: 'Interactions, data and rules become context ATNA can use.' } },
    { n: '04', title: { fr: 'Agir', en: 'Act' }, text: { fr: 'Vos équipes peuvent voir, recommander, automatiser et intervenir à partir d’une même intelligence.', en: 'Your teams can see, recommend, automate and act from one intelligence.' } },
  ] as { n: string; title: Bi; text: Bi }[],
  statement: [
    { fr: 'L’identité nourrit le contexte.', en: 'Identity feeds context.' },
    { fr: 'Le contexte nourrit les services.', en: 'Context feeds services.' },
    { fr: 'Les services nourrissent l’intelligence.', en: 'Services feed intelligence.' },
    { fr: 'L’intelligence augmente votre capacité d’agir.', en: 'Intelligence increases your capacity to act.' },
  ] as Bi[],
  cta: { fr: 'Demander une démo', en: 'Request a demo' } as Bi,
}
